// SkipTraceCsvUploadModal — the "Trace from CSV" flow.
//
// File pick → POST upload (dry run) → three buckets (Auto-attached /
// Needs review / Unknown) with per-row resolution → Commit → results.
//
// The dry run never writes; the operator resolves the review + unknown
// rows, optionally overrides the trace decision, then commits. Commit
// writes recorder addresses (unconditional on a confirmed match) and
// fires Enformion Contact Enrich for the trace subset.

import { useMemo, useState } from 'react';
import {
  leadListApi,
  type CsvUploadResponse, type CsvUploadRow, type CsvMatchCandidate,
  type CsvCommitRow, type CsvCommitResponse, type CsvCommitAction,
} from '../services/leadListApi';
import { partyApi } from '../services/partyApi';
import { HasContactIndicator } from './HasContactIndicator';

interface Props {
  jurisdiction: 'CO' | 'WY';
  onClose: () => void;
  /** Called after a successful commit so the lead list can reload. */
  onCommitted: () => void | Promise<void>;
}

// A flat, mutable per-row decision. Joint-grantor rows are pre-expanded
// into one Reso per half so each half resolves independently.
interface Reso {
  key: string;
  bucket: CsvUploadRow['bucket'];
  reason?: CsvUploadRow['review_reason'];
  data: CsvCommitRow['data'];
  is_entity: boolean;
  is_po_box: boolean;
  is_joint_half: boolean;
  candidates: CsvMatchCandidate[];
  grantor_names?: string[];
  match_tier?: CsvUploadRow['match_tier'];
  // operator decision:
  action: CsvCommitAction;
  party_id: string | null;
  party_label: string | null;
  party_has_phone: boolean;
  trace: boolean;
}

const REASON_LABEL: Record<NonNullable<CsvUploadRow['review_reason']>, string> = {
  joint_grantor:     'joint grantor — split',
  doc_name_mismatch: 'doc name mismatch',
  ambiguous:         'multiple matches',
  fuzzy_review:      'fuzzy match',
  kind_mismatch:     'entity/individual mismatch',
};

const TIER_LABEL: Record<NonNullable<CsvUploadRow['match_tier']>, string> = {
  doc: 'doc#', name_exact: 'name', fuzzy: 'fuzzy',
};

function nameOf(d: CsvCommitRow['data']): string {
  if (d.entity && !d.first && !d.last) return d.entity;
  return [d.first, d.last].filter(Boolean).join(' ');
}
function addrOf(d: CsvCommitRow['data']): string {
  return [d.address, [d.city, d.state].filter(Boolean).join(', '), d.zip].filter(Boolean).join(' · ');
}

// Build the initial decision list from the dry-run response.
function buildResos(rows: CsvUploadRow[]): Reso[] {
  const out: Reso[] = [];
  for (const r of rows) {
    if (r.is_joint && r.split_halves && r.split_halves.length > 0) {
      r.split_halves.forEach((h, i) => {
        out.push({
          key: `${r.row_index}-${i}`,
          bucket: 'review', reason: 'joint_grantor',
          data: { ...r.data, first: h.first, last: h.last, entity: '' },
          is_entity: false, is_po_box: r.is_po_box, is_joint_half: true,
          candidates: [],
          action: 'drop', party_id: null, party_label: null, party_has_phone: false, trace: false,
        });
      });
      continue;
    }
    if (r.bucket === 'auto' && r.matched) {
      out.push({
        key: `${r.row_index}`,
        bucket: 'auto', data: r.data,
        is_entity: r.is_entity, is_po_box: r.is_po_box, is_joint_half: false,
        candidates: [r.matched], match_tier: r.match_tier,
        action: 'attach', party_id: r.matched.party_id,
        party_label: r.matched.display_name_natural ?? r.matched.display_name,
        party_has_phone: r.matched.has_phone,
        trace: !!r.would_trace,
      });
      continue;
    }
    // review (non-joint) or unknown → default to drop; operator resolves.
    out.push({
      key: `${r.row_index}`,
      bucket: r.bucket, reason: r.review_reason, data: r.data,
      is_entity: r.is_entity, is_po_box: r.is_po_box, is_joint_half: false,
      candidates: r.candidates ?? [], grantor_names: r.grantor_names,
      action: 'drop', party_id: null, party_label: null, party_has_phone: false, trace: false,
    });
  }
  return out;
}

export function SkipTraceCsvUploadModal({ jurisdiction, onClose, onCommitted }: Props) {
  const [phase, setPhase] = useState<'pick' | 'uploading' | 'review' | 'committing' | 'done'>('pick');
  const [err, setErr] = useState<string | null>(null);
  const [upload, setUpload] = useState<CsvUploadResponse | null>(null);
  const [resos, setResos] = useState<Reso[]>([]);
  const [result, setResult] = useState<CsvCommitResponse | null>(null);

  async function onFile(file: File) {
    setPhase('uploading'); setErr(null);
    try {
      const resp = await leadListApi.uploadSkipTraceCsv(file, jurisdiction);
      setUpload(resp);
      setResos(buildResos(resp.rows));
      setPhase('review');
    } catch (e: any) {
      setErr(e?.message ?? 'Upload failed');
      setPhase('pick');
    }
  }

  function patch(key: string, next: Partial<Reso>) {
    setResos(prev => prev.map(r => (r.key === key ? { ...r, ...next } : r)));
  }

  // Default trace decision when a party is chosen: trace individuals
  // unless the chosen party already has a phone.
  const defaultTrace = (isEntity: boolean, hasPhone: boolean) => !isEntity && !hasPhone;

  function chooseCandidate(r: Reso, c: CsvMatchCandidate) {
    patch(r.key, {
      action: 'attach', party_id: c.party_id,
      party_label: c.display_name_natural ?? c.display_name,
      party_has_phone: c.has_phone,
      trace: defaultTrace(r.is_entity, c.has_phone),
    });
  }
  function chooseAddNew(r: Reso) {
    patch(r.key, {
      action: 'add_new', party_id: null, party_label: null, party_has_phone: false,
      trace: defaultTrace(r.is_entity, false),
    });
  }
  function chooseDrop(r: Reso) {
    patch(r.key, { action: 'drop', trace: false });
  }

  // Override: trace anyway for auto rows whose matched party already has
  // a phone (the "don't re-spend" default skipped them).
  const [overrideExisting, setOverrideExisting] = useState(false);
  function toggleOverride(on: boolean) {
    setOverrideExisting(on);
    setResos(prev => prev.map(r =>
      r.bucket === 'auto' && r.action === 'attach' && r.party_has_phone && !r.is_entity
        ? { ...r, trace: on }
        : r));
  }

  const counts = useMemo(() => {
    const auto = resos.filter(r => r.bucket === 'auto');
    const review = resos.filter(r => r.bucket === 'review');
    const unknown = resos.filter(r => r.bucket === 'unknown');
    const willTrace = resos.filter(r => r.action !== 'drop' && r.trace).length;
    const willAttach = resos.filter(r => r.action === 'attach').length;
    const willAdd = resos.filter(r => r.action === 'add_new').length;
    return { auto, review, unknown, willTrace, willAttach, willAdd };
  }, [resos]);

  async function commit() {
    const rows: CsvCommitRow[] = resos
      .filter(r => r.action !== 'drop')
      .map(r => ({
        data: r.data,
        action: r.action,
        party_id: r.action === 'attach' ? (r.party_id ?? undefined) : undefined,
        trace: r.trace,
        is_entity: r.is_entity,
      }));
    if (rows.length === 0) { setErr('Nothing to commit — every row is set to drop.'); return; }
    setPhase('committing'); setErr(null);
    try {
      const resp = await leadListApi.commitSkipTraceCsv(rows);
      setResult(resp);
      setPhase('done');
      await onCommitted();
    } catch (e: any) {
      setErr(e?.message ?? 'Commit failed');
      setPhase('review');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={phase === 'pick' || phase === 'done' ? onClose : undefined}>
      <div className="bg-bg-card border border-border rounded-lg w-[920px] max-w-[96vw] max-h-[92vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">enformion contact enrich · recorder addresses</div>
            <div className="font-display text-[22px] leading-tight">⌗ Trace <em className="text-accent not-italic">from CSV</em></div>
          </div>
          <button onClick={onClose} disabled={phase === 'uploading' || phase === 'committing'}
            className="text-text-dim hover:text-text text-2xl leading-none disabled:opacity-40">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {err && (
            <div className="mb-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
          )}

          {(phase === 'pick' || phase === 'uploading') && (
            <div className="py-10 text-center">
              <p className="text-[13px] text-text-dim mb-4">
                Upload the Cowork recorder-grantor CSV (doc_number, first, last, entity, address, city, state, zip).
              </p>
              <label className="inline-block">
                <span className={'px-4 py-2 text-[13px] rounded border cursor-pointer ' +
                  (phase === 'uploading'
                    ? 'border-border text-text-faint'
                    : 'border-accent text-accent hover:bg-accent-soft')}>
                  {phase === 'uploading' ? 'Parsing…' : 'Choose CSV file'}
                </span>
                <input type="file" accept=".csv,text/csv" className="hidden"
                  disabled={phase === 'uploading'}
                  onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
              </label>
            </div>
          )}

          {(phase === 'review' || phase === 'committing') && upload && (
            <>
              <UploadSummary upload={upload} counts={counts}
                overrideExisting={overrideExisting} onToggleOverride={toggleOverride} />

              <Bucket title="Auto-attached" tone="green" rows={resos.filter(r => r.bucket === 'auto')}
                renderRow={r => (
                  <AutoRow key={r.key} r={r}
                    onTrace={t => patch(r.key, { trace: t })}
                    onDrop={() => (r.action === 'drop' ? chooseCandidate(r, r.candidates[0]) : chooseDrop(r))} />
                )} />

              <Bucket title="Needs review" tone="amber" rows={resos.filter(r => r.bucket === 'review')}
                renderRow={r => (
                  <ResolveRow key={r.key} r={r}
                    onCandidate={c => chooseCandidate(r, c)}
                    onAddNew={() => chooseAddNew(r)}
                    onDrop={() => chooseDrop(r)}
                    onTrace={t => patch(r.key, { trace: t })}
                    onAttachSearched={(pid, label, hasPhone) => patch(r.key, {
                      action: 'attach', party_id: pid, party_label: label,
                      party_has_phone: hasPhone, trace: defaultTrace(r.is_entity, hasPhone),
                    })} />
                )} />

              <Bucket title="Unknown" tone="dim" rows={resos.filter(r => r.bucket === 'unknown')}
                renderRow={r => (
                  <ResolveRow key={r.key} r={r}
                    onCandidate={c => chooseCandidate(r, c)}
                    onAddNew={() => chooseAddNew(r)}
                    onDrop={() => chooseDrop(r)}
                    onTrace={t => patch(r.key, { trace: t })}
                    onAttachSearched={(pid, label, hasPhone) => patch(r.key, {
                      action: 'attach', party_id: pid, party_label: label,
                      party_has_phone: hasPhone, trace: defaultTrace(r.is_entity, hasPhone),
                    })} />
                )} />
            </>
          )}

          {phase === 'done' && result && <ResultsPane result={result} />}
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center justify-between gap-3 bg-bg-card-soft/40 shrink-0">
          <div className="text-[11px] font-mono text-text-faint">
            {phase === 'review' && (
              <>will attach {counts.willAttach} · add {counts.willAdd} new · trace {counts.willTrace}</>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={phase === 'uploading' || phase === 'committing'}
              className="px-4 py-2 text-[13px] text-text-dim hover:text-text border border-border rounded disabled:opacity-50">
              {phase === 'done' ? 'Close' : 'Cancel'}
            </button>
            {(phase === 'review' || phase === 'committing') && (
              <button onClick={commit} disabled={phase === 'committing'}
                className="px-4 py-2 text-[13px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
                {phase === 'committing' ? 'Committing…' : `Commit (${counts.willAttach + counts.willAdd} rows)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Summary header ────────────────────────────────────────────────────
function UploadSummary({ upload, counts, overrideExisting, onToggleOverride }: {
  upload: CsvUploadResponse;
  counts: { willTrace: number };
  overrideExisting: boolean;
  onToggleOverride: (on: boolean) => void;
}) {
  const a = upload.aggregates;
  const haveContact = a.parties_with_existing_phone;
  const needTrace = a.parties_to_trace_default;
  return (
    <div className="mb-5">
      <div className="grid grid-cols-4 gap-2 mb-3 text-[12px]">
        <Tile label="Rows"          n={a.total_rows}     color="text-text" />
        <Tile label="Auto-attached" n={a.auto_attached}  color="text-green" />
        <Tile label="Needs review"  n={a.needs_review}   color="text-amber" />
        <Tile label="Unknown"       n={a.unknown}        color="text-text-dim" />
      </div>
      <div className="flex items-center justify-between bg-bg-input/40 border border-border rounded px-3 py-2 text-[12px]">
        <span className="text-text-dim font-mono">
          {haveContact} of {a.auto_attached} auto-matched already have contact · {needTrace} need tracing
          <span className="text-text-faint"> · {counts.willTrace} will trace now</span>
        </span>
        <label className="flex items-center gap-2 text-[11px] text-text-dim cursor-pointer">
          <input type="checkbox" checked={overrideExisting} onChange={e => onToggleOverride(e.target.checked)}
            className="accent-accent" />
          Trace anyway (parties with existing contact)
        </label>
      </div>
      {upload.warnings.length > 0 && (
        <div className="mt-2 text-[11px] text-amber font-mono">
          {upload.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}
    </div>
  );
}

function Tile({ label, n, color }: { label: string; n: number; color: string }) {
  return (
    <div className="bg-bg-input/40 border border-border rounded px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-text-faint font-mono">{label}</div>
      <div className={`text-[18px] font-display mt-0.5 ${color}`}>{n}</div>
    </div>
  );
}

function Bucket({ title, tone, rows, renderRow }: {
  title: string; tone: 'green' | 'amber' | 'dim';
  rows: Reso[]; renderRow: (r: Reso) => React.ReactNode;
}) {
  if (rows.length === 0) return null;
  const toneClass = tone === 'green' ? 'text-green' : tone === 'amber' ? 'text-amber' : 'text-text-dim';
  return (
    <div className="mb-5">
      <div className={`text-[11px] font-mono uppercase tracking-wider mb-2 ${toneClass}`}>{title} · {rows.length}</div>
      <div className="border border-border rounded divide-y divide-border">
        {rows.map(renderRow)}
      </div>
    </div>
  );
}

// ── Auto row: matched party + trace toggle + keep/skip ────────────────
function AutoRow({ r, onTrace, onDrop }: { r: Reso; onTrace: (t: boolean) => void; onDrop: () => void }) {
  const dropped = r.action === 'drop';
  return (
    <div className={'px-3 py-2 text-[12px] flex items-center gap-3 ' + (dropped ? 'opacity-40' : '')}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-text truncate">{nameOf(r.data)}</span>
          {r.match_tier && (
            <span className="shrink-0 text-[9px] font-mono uppercase px-1 rounded bg-green/10 text-green">{TIER_LABEL[r.match_tier]}</span>
          )}
          {r.is_po_box && <span className="shrink-0 text-[9px] font-mono uppercase px-1 rounded bg-amber/10 text-amber" title="P.O. Box — Contact Enrich quality is lower">po box</span>}
          <span className="text-text-faint truncate">→ {r.party_label}</span>
          {r.candidates[0] && <HasContactIndicator hasPhone={r.candidates[0].has_phone} hasAddress={r.candidates[0].has_address} />}
        </div>
        <div className="text-[10px] text-text-faint font-mono truncate">{addrOf(r.data)}</div>
      </div>
      <label className="flex items-center gap-1.5 text-[11px] text-text-dim shrink-0">
        <input type="checkbox" checked={!dropped && r.trace} disabled={dropped || r.is_entity}
          onChange={e => onTrace(e.target.checked)} className="accent-accent" />
        trace
      </label>
      <button onClick={onDrop} className="shrink-0 text-[11px] text-text-faint hover:text-red border border-border rounded px-2 py-0.5">
        {dropped ? 'keep' : 'skip'}
      </button>
    </div>
  );
}

// ── Review / unknown row: candidate select + add-new + drop + search ──
function ResolveRow({ r, onCandidate, onAddNew, onDrop, onTrace, onAttachSearched }: {
  r: Reso;
  onCandidate: (c: CsvMatchCandidate) => void;
  onAddNew: () => void;
  onDrop: () => void;
  onTrace: (t: boolean) => void;
  onAttachSearched: (partyId: string, label: string, hasPhone: boolean) => void;
}) {
  const resolved = r.action !== 'drop';
  return (
    <div className="px-3 py-2 text-[12px]">
      <div className="flex items-center gap-1.5">
        <span className="text-text truncate">{nameOf(r.data) || <span className="text-text-faint italic">(no name)</span>}</span>
        {r.reason && <span className="shrink-0 text-[9px] font-mono uppercase px-1 rounded bg-amber/10 text-amber">{REASON_LABEL[r.reason]}</span>}
        {r.is_entity && <span className="shrink-0 text-[9px] font-mono uppercase px-1 rounded bg-blue/10 text-blue">entity</span>}
        {r.is_po_box && <span className="shrink-0 text-[9px] font-mono uppercase px-1 rounded bg-amber/10 text-amber" title="P.O. Box">po box</span>}
        {resolved && <span className="shrink-0 text-[10px] text-green font-mono">→ {r.action === 'add_new' ? 'new party' : r.party_label}</span>}
      </div>
      <div className="text-[10px] text-text-faint font-mono truncate mb-1.5">{addrOf(r.data)}</div>

      {r.grantor_names && r.grantor_names.length > 0 && (
        <div className="text-[10px] text-text-faint font-mono mb-1.5">
          doc grantors: {r.grantor_names.join(' · ')}
        </div>
      )}

      <div className="flex items-center gap-1.5 flex-wrap">
        {r.candidates.map(c => (
          <button key={c.party_id} onClick={() => onCandidate(c)}
            className={'text-[11px] px-2 py-0.5 rounded border ' +
              (r.party_id === c.party_id ? 'border-accent bg-accent-soft text-accent' : 'border-border text-text-dim hover:text-text')}>
            {c.display_name_natural ?? c.display_name}
            {c.sim != null && <span className="text-text-faint ml-1">{c.sim.toFixed(2)}</span>}
          </button>
        ))}
        <button onClick={onAddNew}
          className={'text-[11px] px-2 py-0.5 rounded border ' +
            (r.action === 'add_new' ? 'border-accent bg-accent-soft text-accent' : 'border-border text-text-dim hover:text-text')}>
          + add new
        </button>
        <button onClick={onDrop}
          className={'text-[11px] px-2 py-0.5 rounded border ' +
            (r.action === 'drop' ? 'border-red/50 bg-red/10 text-red' : 'border-border text-text-faint hover:text-red')}>
          drop
        </button>
        {resolved && !r.is_entity && (
          <label className="flex items-center gap-1 text-[11px] text-text-dim ml-1">
            <input type="checkbox" checked={r.trace} onChange={e => onTrace(e.target.checked)} className="accent-accent" />
            trace
          </label>
        )}
      </div>

      <SearchAttach onPick={onAttachSearched} />
    </div>
  );
}

// Inline party search → attach to an existing party not in the candidate
// list. Used for the review + unknown buckets.
function SearchAttach({ onPick }: { onPick: (partyId: string, label: string, hasPhone: boolean) => void }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Array<{ party_id: string; label: string; has_phone: boolean; has_address: boolean }>>([]);
  const [busy, setBusy] = useState(false);

  async function run(query: string) {
    setQ(query);
    if (query.trim().length < 2) { setResults([]); return; }
    setBusy(true);
    try {
      const r = await partyApi.search(query, { limit: 6 });
      setResults(r.results.map(x => ({
        party_id: x.party_id,
        label: x.display_name_natural ?? x.display_name,
        has_phone: x.has_phone, has_address: x.has_address,
      })));
    } catch { /* surfaced as empty results */ }
    finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="mt-1 text-[10px] text-text-faint hover:text-text font-mono underline-offset-2 hover:underline">
        attach to existing (search)…
      </button>
    );
  }
  return (
    <div className="mt-1.5">
      <input autoFocus value={q} onChange={e => run(e.target.value)}
        placeholder="search parties…"
        className="w-full bg-bg-input border border-border rounded px-2 py-1 text-[12px] font-mono text-text" />
      {busy && <div className="text-[10px] text-text-faint font-mono mt-1">searching…</div>}
      {results.length > 0 && (
        <div className="mt-1 border border-border rounded divide-y divide-border">
          {results.map(x => (
            <button key={x.party_id}
              onClick={() => { onPick(x.party_id, x.label, x.has_phone); setOpen(false); setQ(''); setResults([]); }}
              className="w-full text-left px-2 py-1 text-[11px] hover:bg-bg-hover/40 flex items-center gap-1.5">
              <span className="truncate">{x.label}</span>
              <HasContactIndicator hasPhone={x.has_phone} hasAddress={x.has_address} className="ml-auto" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Results pane ──────────────────────────────────────────────────────
function ResultsPane({ result }: { result: CsvCommitResponse }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-2 mb-5 text-[12px]">
        <Tile label="Addresses written" n={result.addresses_written} color="text-green" />
        <Tile label="New parties"       n={result.parties_created}   color="text-blue" />
        <Tile label="Traced"            n={result.traced}            color="text-text" />
        <Tile label="Matched"           n={result.matched}           color="text-green" />
        <Tile label="No match"          n={result.no_match}          color="text-text-dim" />
        <Tile label="Errors"            n={result.errors}            color="text-red" />
      </div>
      <div className="border border-border rounded divide-y divide-border">
        {result.outcomes.map((o, i) => (
          <div key={i} className="px-3 py-1.5 text-[11px] font-mono">
            <div className="flex items-center gap-3">
              <span className="text-text-dim truncate flex-1">{nameOf(o.data)}</span>
              <span className={o.address_written ? 'text-green' : o.address_duplicate ? 'text-text-faint' : 'text-text-ghost'}>
                {o.address_written ? 'addr ✓' : o.address_duplicate ? 'addr dup' : 'no addr'}
              </span>
              <span className={
                o.trace_status === 'matched' ? 'text-green'
                : o.trace_status === 'error' ? 'text-red'
                : o.trace_status === 'no-match' ? 'text-text-dim' : 'text-text-faint'}>
                {o.trace_status}
                {o.trace_status === 'matched' && ((o.inserted_phones ?? 0) > 0 || (o.inserted_emails ?? 0) > 0)
                  ? ` (+${o.inserted_phones ?? 0}☎ +${o.inserted_emails ?? 0}✉)` : ''}
              </span>
              {o.is_po_box && <span className="text-amber" title="P.O. Box">po</span>}
              {o.deceased && <span className="text-red">decsd</span>}
            </div>
            {o.message && o.trace_status === 'error' && (
              <div className="text-[10px] text-red/80 mt-0.5 break-words" title={o.message}>{o.message}</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
